import {
  Controller,
  DefaultValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Flex, Spacer, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import * as Form from 'components/_core/form';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import { GENDER_OPTIONS } from 'features/Onboarding/modules/Identity/libs/constants';
import { ApplicationType } from 'features/Onboarding/types';

import Header from '../../../../components/Header';
import locales from './locales';
import personalNameSchema, { PersonalNameSchema } from './personalNameSchema';

interface PersonalNameFormProps {
  isSubmitting: boolean;
  defaultValues: DefaultValues<PersonalNameSchema>;
  applicationType: ApplicationType;
  onSubmit: SubmitHandler<PersonalNameSchema>;
}

const PersonalName = asBentoPage<PersonalNameFormProps>(
  ({ defaultValues, isSubmitting, Layout, onPrev, onSubmit }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
      register,
    } = useForm<PersonalNameSchema>({
      defaultValues,
      mode: 'onChange',
      resolver: zodResolver(personalNameSchema),
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header description={locales.description} title={locales.title} />

          <VStack
            align="stretch"
            as="form"
            height="full"
            onSubmit={handleSubmit(onSubmit)}
            spacing="space-40"
          >
            <VStack spacing="space-20">
              <Controller
                control={control}
                name="gender"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    alignSelf="flex-start"
                    direction="row"
                    name="radio-group"
                    onChange={onChange}
                    value={value ?? undefined}
                    variant="outline"
                  >
                    {GENDER_OPTIONS.map((option) => (
                      <Form.Radio key={option.value} value={option.value}>
                        <Form.Radio.Title>
                          {option.abbreviatedLabel}
                        </Form.Radio.Title>
                      </Form.Radio>
                    ))}
                  </Form.Radio.Group>
                )}
              />

              <Form.Input
                placeholder={locales.firstNameLabel}
                {...register('firstName')}
              />

              <Form.Input
                placeholder={locales.lastNameLabel}
                {...register('lastName')}
              />
            </VStack>

            <Spacer />

            <OBStickyButton
              isDisabled={!isValid}
              isLoading={isSubmitting}
              type="submit"
            >
              {locales.continueCta}
            </OBStickyButton>
          </VStack>
        </Flex>
      </Layout>
    );
  },
);

export default PersonalName;
